import React, { useState, useEffect } from 'react';
import nom35AnalyticsApi from '../../api/nom35AnalyticsApi';
import Nom035BarChart from '../results/Nom035BarChart';
import DownloadChartButton from '../DownloadChartButton/DownloadChartButton';
import './Nom035SummaryChart.css';

const levelOrder = ['Nulo','Bajo','Medio','Alto','Muy alto'];

export default function Nom035SummaryChart({
  title,
  companyId,
  applicationId = null,
  applicationIds = [],
  filters: controlledFilters,
  onFiltersChange,
  filterConfigs = [],
  metric = 'global',
  clinical = false,
  rangeLabels = null,
  analyticsApi = nom35AnalyticsApi,
  chartIdPrefix = 'nom035'
}) {
  const [localFilters, setLocalFilters] = useState(
    filterConfigs.reduce((acc, { name }) => ({ ...acc, [name]: '' }), {})
  );
  const filters = controlledFilters ?? localFilters;
  const updateFilters = next => {
    if (controlledFilters !== undefined) onFiltersChange?.(next);
    else setLocalFilters(next);
  };
  const [data, setData] = useState(null); // barras (counts)
  const [loading, setLoading] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState({ loading: false, data: null }); // cache de details
  const applicationIdsKey = applicationIds.join(',');

  const colors = clinical
    ? ['#fd7e14','#dc3545','#00be9c']
    : ['#1069f1','#00be9c','#ffe951','#fd7e14','#dc3545'];

  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    let active = true;
    const loadAll = async () => {
      setLoading(true);
      setData(null);
      setDetails({ loading: true, data: null });

      try {
        const requestFilters = {
          ...JSON.parse(filterKey),
          ...(applicationId ? { applicationId } : {}),
          ...(applicationIdsKey ? { applicationIds: applicationIdsKey } : {}),
        };
        if (clinical) {
          // Solo un endpoint clínico (sin riskLevel)
          const res = companyId
            ? await analyticsApi.getClinicalSummaryByCompanyId(companyId, requestFilters)
            : await analyticsApi.getClinicalSummaryGlobal(requestFilters);

          if (!active) return;
          setData([
            { name: 'Evento traumático',          value: res.counts?.traumaticEvent || 0 },
            { name: 'Requiere atención médica',   value: res.counts?.requireClinicalAttention || 0 },
            { name: 'No requiere atención médica',value: res.counts?.noRequireClinicalAttention || 0 }
          ]);
          setDetails({ loading: false, data: res }); // cachea preguntas clínicas también
        } else {
          // Summary + Details en paralelo (misma metric!)
          const summaryPromise = companyId
            ? analyticsApi.getSummaryByCompanyId(companyId, metric, requestFilters)
            : analyticsApi.getSummaryGlobal(metric, requestFilters);

          const detailsPromise = companyId
            ? analyticsApi.getQuestionsByCompanyId(companyId, metric, requestFilters)
            : analyticsApi.getQuestionsGlobal(metric, requestFilters);

          const [summaryRes, detailsRes] = await Promise.all([summaryPromise, detailsPromise]);

          if (!active) return;
          // barras
          setData(levelOrder.map(lvl => ({ name: lvl, value: summaryRes.counts?.[lvl] || 0 })));
          // cache de detalles
          setDetails({ loading: false, data: detailsRes });
        }
      } catch (err) {
        if (!active) return;
        console.error('Error cargando datos NOM-035:', err);
        setData(null);
        setDetails({ loading: false, data: null });
      } finally {
        if (active) setLoading(false);
      }
    };
    loadAll();
    return () => { active = false; };
  }, [metric, companyId, applicationId, applicationIdsKey, clinical, analyticsApi, filterKey]);

  const handleChange = e => {
    const { name, value } = e.target;
    updateFilters({ ...filters, [name]: value });
  };

  const toggleDetails = () => setShowDetails(prev => !prev);

  const currentRiskLevel = !clinical ? details.data?.riskLevel : null;

  const composedTitle =
    !clinical && currentRiskLevel
      ? `${title}: Riesgo del puntaje promedio ${currentRiskLevel}`
      : title;

  return (
    <div className="nom035-summary-chart">
      <div className="nom035-chart-title-row">
        <h3 className="chart-title">{composedTitle}</h3>
        {data && <DownloadChartButton title={composedTitle} targetSelector=".nom035-summary-chart" />}
      </div>

      <div className="filters">
        {filterConfigs.map(({ name, label, options }) => (
          <div className="filter-item" key={name}>
            <label htmlFor={name}>{label}</label>
            <select id={name} name={name} value={filters[name]} onChange={handleChange}>
              <option value="">Todos</option>
              {options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="chart-and-legend">
        <div className="chart-wrapper">
          {loading
            ? <p>Cargando datos…</p>
            : data
              ? (
                <Nom035BarChart
                  data={data}
                  colors={colors}
                  twoLineLabels={clinical}
                  chartId={`${chartIdPrefix}-${metric}-${companyId || 'global'}`} // <-- útil para keys únicas en el hijo
                />
              )
              : <p>No hay datos para mostrar.</p>
          }
        </div>

        {!clinical && rangeLabels && (
          <div className="legend-section">
            <p className="legend-title">Rango de puntajes:</p>
            <ul className="risk-legend">
              {levelOrder.map((lvl, i) => (
                <li key={lvl}>
                  <span
                    className="legend-bullet"
                    style={{ backgroundColor: colors[i] }}
                  />
                  {lvl} ({rangeLabels[lvl]})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="details-toggle">
        <button onClick={toggleDetails}>
          {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
        </button>
      </div>

      {showDetails && (
        <div className="details">
          {details.loading
            ? <p>Cargando detalles…</p>
            : details.data ? (
                clinical ? (
                  <>
                    <p style={{ color: 'black' }}>
                      Evento traumático:{' '}
                      <strong style={{ color: 'black' }}>
                        {details.data.counts?.traumaticEvent || 0}
                      </strong>
                    </p>
                    <p style={{ color: 'black' }}>
                      Requiere atención médica:{' '}
                      <strong style={{ color: 'black' }}>
                        {details.data.counts?.requireClinicalAttention || 0}
                      </strong>
                    </p>
                    <p style={{ color: 'black' }}>
                      No requiere atención médica:{' '}
                      <strong style={{ color: 'black' }}>
                        {details.data.counts?.noRequireClinicalAttention || 0}
                      </strong>
                    </p>
                    {details.data.questions?.map(q => (
                      <p key={q.field} style={{ color: 'black' }}>
                        {q.question}:{' '}
                        <strong style={{ color: q.average >= 3 ? 'red' : 'black' }}>
                          {q.average}
                        </strong>
                      </p>
                    ))}
                  </>
                ) : (
                  <>
                    {/* riskLevel removido de aquí — ahora va en el título */}
                    {typeof details.data.averageScore === 'number' && (
                      <p style={{ color: 'black' }}>
                        Puntaje promedio:{' '}
                        <strong style={{ color: 'black' }}>
                          {details.data.averageScore.toFixed(2)}
                        </strong>
                      </p>
                    )}
                    {details.data.respondentCount === 0 && <p>Sin respuestas para los filtros seleccionados.</p>}
                    {details.data.respondentCount > 0 && (
                      <p>Promedios sobre {details.data.respondentCount} {details.data.respondentCount === 1 ? 'cuestionario' : 'cuestionarios'}. Las preguntas no aplicables aportan 0 al puntaje total.</p>
                    )}
                    <ul className="details-list">
                      {details.data.questions?.map(q => (
                        <li key={q.field} style={{ color: 'black' }}>
                          {q.question}:{' '}
                          <strong style={{ color: q.average >= 3 ? 'red' : 'black' }}>
                            {q.average == null ? 'Sin respuestas aplicables' : q.average.toFixed(2)}
                          </strong>
                          {q.responseCount != null && ` (${q.responseCount} ${q.responseCount === 1 ? 'respuesta' : 'respuestas'})`}
                        </li>
                      ))}
                    </ul>
                  </>
                )
              )
            : <p>No hay detalles para mostrar.</p>
          }
        </div>
      )}
    </div>
  );
}
